import { useEffect, useState, useSyncExternalStore } from "react";

import {
  forgetShownInvitationLink,
  readShownInvitationLink,
  registerInvitationLinkHost,
  subscribeShownInvitationLink,
} from "~/neucleos/invitationLinkOnce";
import type { InvitationLinkShownOnce } from "~/neucleos/gateInstancesClient";
import {
  AlertDialog,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";

/** What the window says when the World could not give the link an address (no HTTPS return address is set). */
export const INVITATION_LINK_NO_ADDRESS =
  "The invitation was made, but Neucleos could not give its link an address, so it cannot be shown. Withdraw this invitation and make a new one.";

/** "Invitation link for a@b.c", or a plain title when the address is unknown; "No link was made" when none was (P5b). */
export const invitationLinkTitle = (link: InvitationLinkShownOnce): string =>
  link.status === "shown-before"
    ? "This link was already shown"
    : link.status === "not-made"
      ? "No link was made"
      : link.email
        ? `Invitation link for ${link.email}`
        : "Invitation link";

/**
 * #1730 (W-9bc79724 as ruled 9 Oct 2026): the invitation link the founder's own Approve made on this device, shown
 * once over whichever board decided it (the Inbox, the agent strip's popover). It stays until they close it, and closing
 * forgets it: nothing on this device or at the World can show it again. Mounted once, at the root.
 */
export function InvitationLinkOnceHost() {
  // While mounted, links are held for it; unmounted, every held link is forgotten (invitationLinkOnce.ts).
  useEffect(() => registerInvitationLinkHost(), []);
  const link = useSyncExternalStore(
    subscribeShownInvitationLink,
    readShownInvitationLink,
    readShownInvitationLink,
  );
  // Keyed by the link, so the next one waiting starts with its own Copy state.
  return link === null ? null : (
    <InvitationLinkOnce
      key={link.url ?? link.sentence}
      link={link}
      onClose={forgetShownInvitationLink}
    />
  );
}

export function InvitationLinkOnce({
  link,
  onClose,
}: {
  readonly link: InvitationLinkShownOnce;
  readonly onClose: () => void;
}) {
  const [copied, setCopied] = useState<"copied" | "failed" | null>(null);
  const copy = () => {
    if (link.url === null) return;
    void navigator.clipboard.writeText(link.url).then(
      () => setCopied("copied"),
      () => setCopied("failed"),
    );
  };
  const expires =
    link.status === "made" && link.expiresAt !== null
      ? `It works until ${new Date(link.expiresAt).toLocaleString()}.`
      : null;
  return (
    // Escape and a click outside do not close it: losing the link is final, so only Done does.
    <AlertDialog open onOpenChange={() => {}}>
      <AlertDialogPopup className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>{invitationLinkTitle(link)}</AlertDialogTitle>
          <AlertDialogDescription>
            {link.status === "made" && link.url === null
              ? INVITATION_LINK_NO_ADDRESS
              : link.sentence}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {link.status === "made" && link.url !== null ? (
          <div className="flex flex-col gap-2 px-6">
            <input
              readOnly
              aria-label="Invitation link"
              className="w-full rounded-md border border-border bg-muted px-2 py-1.5 font-mono text-xs"
              value={link.url}
              onFocus={(event) => event.currentTarget.select()}
            />
            {expires ? <p className="text-muted-foreground text-xs">{expires}</p> : null}
            {copied === "failed" ? (
              <p className="text-destructive text-xs">
                It could not be copied. Select the link and copy it yourself.
              </p>
            ) : null}
          </div>
        ) : null}
        <AlertDialogFooter>
          {link.status === "made" && link.url !== null ? (
            <Button variant="outline" onClick={copy}>
              {copied === "copied" ? "Copied" : "Copy link"}
            </Button>
          ) : null}
          <Button onClick={onClose}>Done</Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
